import React, {forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import LazyImage from "../../../../../../../../components/app-specific/lazy-image";
import Slider from "react-slick";
import {UserHomepageComponentTypes, UserHomepageControlPanelEvents, UserHomepageEventEmitter} from "../../../../../../models";
import {UserHomepageControllerContext, UserHomepageViewContext} from "../../../../../../index";
import ObserversService from "../../../../../../../../../core/services/observers";
import {bannerWidthToHeightRatio} from "../../../../../../../../../core/constants/enums";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";

const UserPhoneHomepageSlideShow = forwardRef(({component, tabId}, ref) => {
    const controller = useContext(UserHomepageControllerContext);
    const {zoom} = useContext(UserHomepageViewContext);
    const [activeIndex, setActiveIndex] = useState(0);
    const [bannerHeight, setBannerHeight] = useState(187);
    const dragging = useRef(false);
    /**@type {React.MutableRefObject<import('@types/react-slick').Slider>}*/
    const slickSliderRef = useRef();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const paddingMeasurerRef = useRef();
    const isMounted = useIsMounted();

    const banners = useMemo(() => component?.content?.banners ?? [], [component]);

    /**
     * Exposes the slick api ref functionalities to the parent component.
     */
    useImperativeHandle(ref, () => slickSliderRef.current, [slickSliderRef])

    /**
     * With each change in the controller:
     * - attaches the event listeners for content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        return () => {
            controller.off(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        }
    }, [component, controller])

    /**
     * With each change in the zoom value of the view context:
     * - attaches an observer to recompute the height of the banners and to adjust the size of the slider accordingly
     */
    useEffect(() => {
        if (!containerRef.current || !paddingMeasurerRef.current)
            return;
        const throttledCallback = debounce((entries, observer) => adjustBannersSize(entries, observer), 100);
        const observer = ObserversService.newResizeObserver(throttledCallback);
        ObserversService.observeResizeObserver(observer, containerRef.current);
        throttledCallback([containerRef.current]);
        return () => ObserversService.disconnectResizeObserver(observer)
    }, [containerRef, paddingMeasurerRef, zoom])

    /**
     * Recomputes the height of the banners and to adjust the size of the slider accordingly
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const adjustBannersSize = (entries) => {
        requestAnimationFrame(() => {
            if (!isMounted())
                return;
            const entry = entries[0];
            const width = entry?.contentRect?.width ?? entry?.getBoundingClientRect()?.width ?? 0;
            const paddingRight = window.getComputedStyle(paddingMeasurerRef.current)?.paddingRight ?? '0px';
            const parsedPaddingRight = parseFloat(paddingRight.substring(0, paddingRight.length - 2));
            const padding = Number.isNaN(parsedPaddingRight) ? 0 : parsedPaddingRight * 2;
            setBannerHeight((((width - padding) / bannerWidthToHeightRatio)));
        })
    }


    /**
     * Uses the slick slider api to bring the selected content into view.
     *
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    const focusComponentContent = (tabId, componentId, contentId) => {
        // only componentId is checked since each component has a unique id in the server.
        if (componentId !== component.id || component.type?.id !== UserHomepageComponentTypes.slideShow)
            return;
        const contentIndex = component.content?.parts?.findIndex(e => e.id === contentId);
        if (contentIndex === -1)
            return;
        slickSliderRef.current.slickGoTo(contentIndex);
    }

    /**
     * Turns the dragging flag true so that user clicks would have no effects during the dragging phase.
     */
    const beforeSlideChange = () => {
        dragging.current = true;
    }

    /**
     * Invokes the appropriate callback of the controller for the click of the banner.
     * @param banner
     */
    const onBannerClicked = (banner) => {
        if (dragging.current)
            return
        controller.clickContent(tabId, component.id, banner.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate callback of the controller for the selection of the banner.
     * @param banner
     */
    const onBannerSelected = (banner) => {
        controller.selectContent(tabId, component.id, banner.id);
    }

    /**
     * sets the current active index in the state and invokes the appropriate callback of the controller for the
     * selection of the banner
     * @param index
     */
    const afterSlideChange = (index) => {
        dragging.current = false;
        setActiveIndex(index);
        onBannerSelected(banners[index]);
    }


    /**
     * Changes the carousel item based on the given index.
     * @param {number} index
     */
    const changeSlide = (index) => {
        if (index === activeIndex)
            return
        slickSliderRef.current.slickGoTo(index);
    }

    return (
        <>
            <div className={'invisible'} ref={paddingMeasurerRef}/>
            <div ref={containerRef} className={classnames('user-phone-homepage-slideshow')}>
                <div>
                    <Slider
                        infinite
                        draggable
                        initialSlide={activeIndex}
                        ref={slickSliderRef}
                        slidesToShow={1}
                        slidesToScroll={1}
                        centerPadding={'1rem'}
                        arrows={false}
                        speed={250}
                        afterChange={afterSlideChange}
                        beforeChange={beforeSlideChange}
                    >
                        {banners?.map((banner) => (
                            <div
                                onClick={() => onBannerClicked(banner)}
                                key={banner.id}
                                id={banner.phoneViewElementId}
                                className={'image'}
                            >
                                <LazyImage
                                    src={banner?.imageName}
                                    alt={banner?.imageName ?? ''}
                                    height={bannerHeight}
                                />
                            </div>
                        ))}
                    </Slider>
                    <div/>
                    <div className={'dots'}>
                        {banners?.map((banner, index) => (
                            <div
                                key={banner.id}
                                className={classnames(
                                    'dot',
                                    {
                                        'active': index === activeIndex,
                                        'active-minus-one': (index === activeIndex + 1) || (index === activeIndex - 1),
                                        'active-minus-two': (index === activeIndex + 2) || (index === activeIndex - 2),
                                        'active-minus-three': (index === activeIndex + 3) || (index === activeIndex - 3),
                                    }
                                )}
                                onClick={() => changeSlide(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
})

export default UserPhoneHomepageSlideShow;
